import { PosEvent } from '@core/bus/events'
import type { Plugin, PluginContext, HandlerResult } from '@core/bus/types'
import type { PaymentRegistry } from '@core/payments/registry'
import { PAYMENT_METHODS } from '@core/payments/types'
import type { PaymentProvider, PaymentQr, PaymentQrRequest } from '@core/payments/types'
import { debugLog } from '@core/utils/log'

/**
 * Публичный API плагина СБП.
 *
 * Реализует порт ядра `PaymentProvider`: UI берёт провайдера из реестра по
 * способу оплаты и не импортирует этот модуль.
 */
export interface RuSbpPlugin extends Plugin, PaymentProvider {}

/**
 * Плагин «СБП» (агрегатор NewPay).
 *
 * Подписывается на PAYMENT_REQUESTED. Имитирует обращение к API NewPay:
 * создаёт операцию с динамической привязкой суммы чека к статической
 * QR-наклейке кассы, формирует QR-payload и передаёт его в UI-резолвер.
 * Резолвер показывает модалку с QR и ждёт статус транзакции (имитация
 * WebSocket). Результат транслируется в HandlerResult:
 *  - succeeded → ok: true (оплата прошла);
 *  - failed/cancelled → veto (оплата не состоялась, SaleService прервёт продажу).
 */
export function createRuSbpPlugin(): RuSbpPlugin {
  /** Реестр ядра: оттуда берём UI-резолвер оплаты. */
  let payments: PaymentRegistry | null = null

  /** Имитация создания операции в NewPay с динамической суммой. */
  async function createNewPayOperation(
    receiptId: string,
    amount: number
  ): Promise<{ operationId: string; qrPayload: string }> {
    await new Promise((r) => setTimeout(r, 120))
    const operationId = `NP-${Date.now().toString(36)}`
    // Динамическая привязка: сумма зашита в payload СБП-ссылки.
    const amountKopecks = Math.round(amount * 100)
    const qrPayload = `https://qr.nspk.ru/newpay/${operationId}?sum=${amountKopecks}&cur=RUB&rcpt=${receiptId}`
    return { operationId, qrPayload }
  }

  /** Порт ядра `PaymentProvider`: создать операцию и вернуть QR для UI. */
  async function createQr(req: PaymentQrRequest): Promise<PaymentQr> {
    const { operationId, qrPayload } = await createNewPayOperation(req.receiptId, req.amount)
    return { providerId: 'ru-sbp', operationId, payload: qrPayload }
  }

  async function handlePaymentRequested(payment: {
    receiptId: string
    amount: number
    method: string
  }): Promise<HandlerResult> {
    // Плагин СБП обрабатывает только оплату методом 'sbp'. Для наличных/карты
    // ничего не делаем — пропускаем без модалки.
    if (payment.method !== 'sbp') {
      return { ok: true }
    }

    debugLog(
      `[ru-sbp] PAYMENT_REQUESTED: чек ${payment.receiptId}, сумма ${payment.amount}`
    )

    const resolver = payments?.getResolver()
    if (!resolver) {
      return {
        ok: false,
        vetoReason: 'Оплата СБП недоступна: интерфейс QR не инициализирован'
      }
    }

    const request: PaymentQrRequest = {
      receiptId: payment.receiptId,
      amount: payment.amount,
      method: payment.method
    }
    const qr = await createQr(request)
    const outcome = await resolver({ request, qr })

    switch (outcome.status) {
      case 'succeeded':
        debugLog(`[ru-sbp] операция ${qr.operationId} оплачена`)
        return { ok: true }
      case 'cancelled':
        return { ok: false, vetoReason: 'Оплата по СБП отменена' }
      case 'failed':
      default:
        return {
          ok: false,
          vetoReason: `Оплата по СБП не прошла: ${outcome.reason ?? 'отказ агрегатора'}`
        }
    }
  }

  return {
    id: 'ru-sbp',
    country: 'RU',

    init(ctx: PluginContext): void {
      debugLog('[ru-sbp] init')
      payments = ctx.payments
      ctx.bus.on(PosEvent.PAYMENT_REQUESTED, (payment) => handlePaymentRequested(payment))
    },

    supports(method: string): boolean {
      return method === PAYMENT_METHODS.sbp
    },

    createQr
  }
}

/** Готовый экземпляр плагина для регистрации в bootstrap. */
export const ruSbpPlugin: RuSbpPlugin = createRuSbpPlugin()
